import { Progress, TooltipV2, Text, BadgeV3 } from '@cimplico/components';
import { CalculatorIcon, ShareIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { Calculation } from '../../../types/worksheet';
import { getFieldCount } from '../custom-worksheet-v2/store/utils';
import plural from '../../../utilities/plural';
import { useFirmContext } from '../../../providers/FirmProvider';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import RelativeTimestamp from '../../../components/RelativeTimestamp';
import CalculationActions from './CalculationActions';
import { ForwardIcon } from '@heroicons/react/24/outline';

type CalculationCardProps = {
    calculation: Calculation;
    worksheetId: string;
    priorDetails?: {
        // if prior calculation card, pass in required details
        priorWorkpaperId: string;
        wsHasAccount: boolean;
        disableCarryForward: boolean;
        disableCarryForwardHint?: string;
    };
};

function CalculationCard({ calculation, worksheetId, priorDetails }: CalculationCardProps): JSX.Element {
    const { shortId } = useFirmContext();
    const { id: contextWorkpaperId } = useWorkpaperContext();
    // sets workpaperId used in link to either the current or the prior workpaper
    const workpaperId = priorDetails?.priorWorkpaperId ?? contextWorkpaperId;

    const { total, completed } = getFieldCount(calculation.schema, true);

    const progress = total === 0 ? 0 : Math.floor((completed / total) * 100);

    const collection = priorDetails
        ? priorDetails?.wsHasAccount
            ? 'trial-balance'
            : 'other-schedules'
        : location.href.includes('trial-balance')
        ? 'trial-balance'
        : 'other-schedules';

    const link = `/${shortId}/workpapers/${workpaperId}/${collection}/${worksheetId}?tab=calculations&calculationId=${calculation.id}`;
    return (
        <Link
            key={calculation.id}
            className="border rounded p-4 hover:bg-gray-50 min-h-[60px] flex flex-col gap-2 relative"
            to={link}
            target={priorDetails ? '_blank' : undefined}
        >
            <div className="flex items-center gap-1">
                <CalculatorIcon className="h-4 w-4 text-green-600 shrink-0" />
                <Text className="text-sm font-bold text-gray-800 ml-1">{calculation.name}</Text>
                <span className="ml-auto mr-6">
                    <Progress type="circle" percent={progress} showProgressLabel size="small" />
                </span>
                {!priorDetails ? (
                    <CalculationActions calculation={calculation} worksheetId={worksheetId} />
                ) : (
                    // if not a prior calc card, pass in details required to facilitate carry forward action
                    <CalculationActions
                        calculation={calculation}
                        worksheetId={worksheetId}
                        priorDetails={{
                            priorWorkpaperId: priorDetails.priorWorkpaperId,
                            disableCarryForward: priorDetails.disableCarryForward,
                            disableCarryForwardHint: priorDetails.disableCarryForwardHint,
                        }}
                    />
                )}
            </div>
            <Text className="text-xs text-gray-500" lines={2}>
                {calculation.description ?? ''}
            </Text>
            <div className="flex items-center mt-2 justify-between">
                <div className="flex items-center gap-1">
                    <ShareIcon className="text-gray-600 h-4 w-4" />
                    <label className="text-xs text-gray-500">
                        <TooltipV2 message={calculation.worksheets.map((ws) => ws.name)}>
                            {calculation.worksheets.length} {plural(calculation.worksheets.length, 'worksheet')}
                        </TooltipV2>
                    </label>
                </div>
                <RelativeTimestamp className="text-xs text-gray-500" date={calculation.updatedAt} prefix="Edited" />
            </div>
            {/* if not a prior calc card & has been carried forward - display badge */}
            {!priorDetails && calculation.priorCalculationId && (
                <span className="mt-2">
                    <BadgeV3
                        size="small"
                        rounded={false}
                        color="indigo"
                        label="Carried Forward"
                        avatar={<ForwardIcon className="text-indigo-600 h-4 w-4" />}
                    />
                </span>
            )}
        </Link>
    );
}

export default CalculationCard;
